import { gql as apolloGql, useMutation, useQuery } from '@apollo/client'
import { Avatar, Box, Button, Flex, HStack, IconButton, Spinner, Text, Textarea, VStack } from '@chakra-ui/react'
import dayjs from 'dayjs'
import { useAccessStore } from 'features/Access/infra/react/AccessState'
import { CopyIcon, EditIcon, TrashIcon } from 'presentation/components/Icons'
import { Tooltip } from 'presentation/components/Tooltip'
import { toast } from 'presentation/components/Toast'
import { UserAvatar } from 'presentation/components/UserAvatarV2'
import { API_URL } from 'presentation/const/env.const'
import { useEffect, useMemo, useState } from 'react'

const GENERIC_AVATAR_URL = 'https://www.gravatar.com/avatar/?d=mp'

const PROPERTY_DETAILS_MARKETING_LIST_NOTES = apolloGql`
  query PropertyDetailsMarketingListNotes($parcelId: String!) {
    parcel(id: $parcelId) {
      id
      attachments(types: [TEXT_NOTE, AUDIO_NOTE]) {
        __typename
        ... on MarketingListTextAttachment {
          id
          noteId
          parcelId
          attachmentId
          mimeType
          extension
          name
          createdAt
          updatedAt
          createdBy
          content
        }
        ... on MarketingListAudioAttachment {
          id
          noteId
          parcelId
          attachmentId
          mimeType
          extension
          name
          createdAt
          updatedAt
          createdBy
          link
          duration
        }
      }
    }
  }
`

const CREATE_MARKETING_LIST_TEXT_NOTE = apolloGql`
  mutation PropertyDetailsCreateMarketingListTextNote(
    $input: MarketingListAttachmentCreateTextInput!
  ) {
    marketingListAttachmentCreateText(input: $input) {
      __typename
      ... on MarketingListTextAttachment {
        id
      }
      ... on MarketingListBadRequestError {
        message
        field
      }
      ... on MarketingListNotFoundError {
        message
        field
      }
    }
  }
`

const UPDATE_MARKETING_LIST_TEXT_NOTE = apolloGql`
  mutation PropertyDetailsUpdateMarketingListTextNote(
    $input: MarketingListAttachmentUpdateTextInput!
  ) {
    marketingListAttachmentUpdateText(input: $input) {
      __typename
      ... on MarketingListTextAttachment {
        id
      }
      ... on MarketingListBadRequestError {
        message
        field
      }
      ... on MarketingListNotFoundError {
        message
        field
      }
    }
  }
`

const DELETE_MARKETING_LIST_NOTE = apolloGql`
  mutation PropertyDetailsDeleteMarketingListNote(
    $input: MarketingListAttachmentDeleteInput!
  ) {
    marketingListAttachmentDelete(input: $input) {
      __typename
      ... on MarketingListAttachmentDeleteSuccess {
        noteId
      }
      ... on MarketingListBadRequestError {
        message
        field
      }
      ... on MarketingListNotFoundError {
        message
        field
      }
    }
  }
`

type MarketingListAttachmentBase = {
  id: string
  noteId: string
  parcelId: string
  attachmentId: string | null
  mimeType: string | null
  extension: string | null
  name: string | null
  createdAt: string | null
  updatedAt: string | null
  createdBy: string | null
}

type MarketingListTextAttachment = MarketingListAttachmentBase & {
  __typename: 'MarketingListTextAttachment'
  content: string | null
}

type MarketingListAudioAttachment = MarketingListAttachmentBase & {
  __typename: 'MarketingListAudioAttachment'
  link: string | null
  duration: number | null
}

type MarketingListAttachment =
  | MarketingListTextAttachment
  | MarketingListAudioAttachment

type NotesQueryData = {
  parcel: {
    id: string
    attachments: MarketingListAttachment[]
  } | null
}

type NotesQueryVariables = {
  parcelId: string
}

type AttachmentMutationInput = {
  parcelId: string
  noteId: string
}

type CreateTextNoteInput = {
  parcelId: string
  content: string
}

type MutationError = {
  __typename: 'MarketingListBadRequestError' | 'MarketingListNotFoundError'
  message: string | null
  field: string | null
}

type UpdateTextNoteData = {
  marketingListAttachmentUpdateText:
    | { __typename: 'MarketingListTextAttachment', id: string }
    | MutationError
}

type CreateTextNoteData = {
  marketingListAttachmentCreateText:
    | { __typename: 'MarketingListTextAttachment', id: string }
    | MutationError
}

type DeleteNoteData = {
  marketingListAttachmentDelete:
    | {
      __typename: 'MarketingListAttachmentDeleteSuccess'
      noteId: string
    }
    | MutationError
}

type EditingNote = {
  noteId: string
  content: string
}

type NoteGroup = {
  key: string
  label: string
  notes: MarketingListAttachment[]
}

type NotesState = {
  notes: MarketingListAttachment[]
  loading: boolean
  refetch: () => Promise<unknown>
}

export const usePropertyDetailsMarketingListNotes = (
  parcelId: string | null | undefined,
): NotesState => {
  const { data, error, loading, refetch } = useQuery<
    NotesQueryData,
    NotesQueryVariables
  >(PROPERTY_DETAILS_MARKETING_LIST_NOTES, {
    variables: { parcelId: parcelId || '' },
    skip: !parcelId,
    fetchPolicy: 'cache-and-network',
  })

  useEffect(() => {
    if (!error) return

    toast.error({
      id: 'PROPERTY_DETAILS_NOTES_LOAD_ERROR',
      title: 'Failed to Load Notes',
      message: 'Please try again or contact support.',
    })
  }, [error])

  return {
    notes: data?.parcel?.attachments || [],
    loading,
    refetch,
  }
}

export const NotesSection = ({
  parcelId,
  notes,
  loading,
  refetch,
}: {
  parcelId: string
} & NotesState) => {
  const [editingNote, setEditingNote] = useState<EditingNote | null>(null)
  const [newNoteContent, setNewNoteContent] = useState('')
  const [createTextNote, createTextNoteState] = useMutation<
    CreateTextNoteData,
    { input: CreateTextNoteInput }
  >(CREATE_MARKETING_LIST_TEXT_NOTE, {
    fetchPolicy: 'no-cache',
  })
  const [updateTextNote, updateTextNoteState] = useMutation<
    UpdateTextNoteData,
    { input: AttachmentMutationInput & { content: string } }
  >(UPDATE_MARKETING_LIST_TEXT_NOTE, {
    fetchPolicy: 'no-cache',
  })
  const [deleteNote, deleteNoteState] = useMutation<
    DeleteNoteData,
    { input: AttachmentMutationInput }
  >(DELETE_MARKETING_LIST_NOTE, {
    fetchPolicy: 'no-cache',
  })

  useEffect(() => {
    setEditingNote(null)
    setNewNoteContent('')
  }, [parcelId])

  const currentUserId = useAccessStore(state => {
    const access = state.local.access

    if (
      (access.status === 'loaded' || access.status === 'updating')
      && access.data.status === 'logged-in'
    )
      return access.data.accessor.id


    return null
  })
  const noteGroups = useMemo(() => groupNotes(notes), [notes])
  const isMutating = createTextNoteState.loading
    || updateTextNoteState.loading
    || deleteNoteState.loading

  const createNote = async () => {
    const content = newNoteContent

    if (!content.trim()) {
      toast.error({
        title: 'Unable to Save Note',
        message: 'Note content is required.',
      })
      return
    }

    const result = await createTextNote({
      variables: {
        input: {
          parcelId,
          content,
        },
      },
    })
    const payload = result.data?.marketingListAttachmentCreateText

    if (payload?.__typename !== 'MarketingListTextAttachment')
      throw new Error(getMutationErrorMessage(payload, 'Failed to save note.'))

    setNewNoteContent('')
    await refetch()
  }

  const saveNote = async (note: MarketingListAttachment) => {
    const content = editingNote?.content || ''

    if (!content.trim()) {
      toast.error({
        title: 'Unable to Save Note',
        message: 'Note content is required.',
      })
      return
    }

    const result = await updateTextNote({
      variables: {
        input: {
          ...getAttachmentMutationInput(note, parcelId),
          content,
        },
      },
    })
    const payload = result.data?.marketingListAttachmentUpdateText

    if (payload?.__typename !== 'MarketingListTextAttachment')
      throw new Error(getMutationErrorMessage(payload, 'Failed to save note.'))


    setEditingNote(null)
    await refetch()
  }

  const removeNote = async (note: MarketingListAttachment) => {
    const result = await deleteNote({
      variables: {
        input: getAttachmentMutationInput(note, parcelId),
      },
    })
    const payload = result.data?.marketingListAttachmentDelete

    if (payload?.__typename !== 'MarketingListAttachmentDeleteSuccess') {
      throw new Error(
        getMutationErrorMessage(payload, 'Failed to delete note.'),
      )
    }

    await refetch()
  }

  if (!parcelId) return null

  return (
    <Box
      bg='neutral.100'
      borderRadius={2}
      minH='220px'
      position='relative'
      px={{ base: 2, tabSm: 3 }}
      py={3}
    >
      {loading
        ? (
          <Spinner
            color='accent.blue-text'
            position='absolute'
            right={2}
            top={2}
            size='sm'
          />
        )
        : null}
      <VStack spacing={3} align='stretch'>
        {noteGroups.map(group => (
          <Box key={group.key}>
            <HStack justifyContent='center' mb={1}>
              <Text
                textStyle='bodyS'
                color='ondark.1'
                bg='card.bg.blue'
                px={2}
                py={0.5}
                borderRadius={1}
              >
                {group.label}
              </Text>
            </HStack>
            <VStack spacing={0} align='stretch'>
              {group.notes.map((note, index) => (
                <NoteRow
                  key={note.id}
                  note={note}
                  notes={group.notes}
                  index={index}
                  currentUserId={currentUserId}
                  isBusy={isMutating}
                  editingNote={editingNote}
                  onEdit={() => {
                    const content
                      = note.__typename === 'MarketingListTextAttachment'
                        ? note.content || ''
                        : ''

                    setEditingNote({
                      noteId: note.noteId,
                      content,
                    })
                  }}
                  onCancelEdit={() => setEditingNote(null)}
                  onEditContentChange={content => {
                    setEditingNote({
                      noteId: note.noteId,
                      content,
                    })
                  }}
                  onSave={() => {
                    void saveNote(note).catch(reportSaveError)
                  }}
                  onDelete={() => {
                    void removeNote(note).catch(reportDeleteError)
                  }}
                  onCopy={() => {
                    void copyNote(note).catch(reportCopyError)
                  }}
                />
              ))}
            </VStack>
          </Box>
        ))}
        <NoteComposer
          currentUserId={currentUserId}
          content={newNoteContent}
          isBusy={isMutating}
          onContentChange={setNewNoteContent}
          onCreate={() => {
            void createNote().catch(reportCreateError)
          }}
        />
      </VStack>
    </Box>
  )
}

const NoteComposer = ({
  currentUserId,
  content,
  isBusy,
  onContentChange,
  onCreate,
}: {
  currentUserId: string | null
  content: string
  isBusy: boolean
  onContentChange: (content: string) => void
  onCreate: () => void
}) => (
  <Box px={{ base: 0, tabSm: 2 }} pt={2}>
    <Flex alignItems='flex-end' gap={2} justifyContent='flex-end'>
      <Box
        bg='neutral.200'
        borderColor='accent.blue-text'
        borderTopLeftRadius={2}
        borderTopRightRadius={2}
        borderBottomLeftRadius={2}
        borderBottomRightRadius={0}
        borderWidth='1px'
        boxShadow='primary.s'
        minW={0}
        px={3}
        py={2}
        w={{ base: 'calc(100% - 40px)', tabSm: '72%' }}
      >
        <Textarea
          value={content}
          onChange={event => onContentChange(event.target.value)}
          placeholder='Add a note'
          resize='vertical'
          minH='88px'
          bg='card.bg.1'
          isDisabled={isBusy}
          w='full'
        />
      </Box>
      <Box boxSize='7' flexShrink={0}>
        {currentUserId
          ? (
            <UserAvatar
              id={currentUserId}
              colorScheme='specialsat.700'
              size='lg'
            />
          )
          : (
            <Avatar
              colorScheme='specialsat.700'
              size='lg'
              src={GENERIC_AVATAR_URL}
            />
          )}
      </Box>
    </Flex>
    <HStack justifyContent='flex-end' spacing={2} mt={2} pr={10}>
      <Button
        size='sm'
        colorScheme='blue'
        onClick={onCreate}
        isLoading={isBusy}
        isDisabled={!content.trim()}
      >
        Save
      </Button>
    </HStack>
  </Box>
)

const NoteRow = ({
  note,
  notes,
  index,
  currentUserId,
  isBusy,
  editingNote,
  onEdit,
  onCancelEdit,
  onEditContentChange,
  onSave,
  onDelete,
  onCopy,
}: {
  note: MarketingListAttachment
  notes: MarketingListAttachment[]
  index: number
  currentUserId: string | null
  isBusy: boolean
  editingNote: EditingNote | null
  onEdit: () => void
  onCancelEdit: () => void
  onEditContentChange: (content: string) => void
  onSave: () => void
  onDelete: () => void
  onCopy: () => void
}) => {
  const canMutate = !!note.parcelId && !!note.noteId
  const isEditing
    = canMutate && !!editingNote && editingNote.noteId === note.noteId
  const isTextNote = note.__typename === 'MarketingListTextAttachment'
  const isAudioNote = note.__typename === 'MarketingListAudioAttachment'
  const noteTitle = getNoteTitle(note)
  const canCopy = isTextNote && !!note.content
  const hasActions = canCopy || (canMutate && isTextNote) || canMutate
  const isOwnNote = areIdsEqual(note.createdBy, currentUserId)
  const { isPrevMerged, isNextMerged } = getNoteMergeState(notes, index)

  return (
    <Box
      role='group'
      bg={isEditing ? 'neutral.200' : undefined}
      borderRadius={2}
      position='relative'
      px={{ base: 0, tabSm: 2 }}
      pt={isPrevMerged && !isEditing ? 0.5 : 2}
      pb={isNextMerged && !isEditing ? 0.5 : 2}
      _focusWithin={{ bg: 'neutral.200' }}
      _hover={{ bg: 'neutral.200' }}
    >
      <Flex
        alignItems='flex-end'
        gap={2}
        justifyContent={isOwnNote ? 'flex-end' : 'flex-start'}
      >
        {!isOwnNote
          ? (
            <NoteAvatar note={note} isHidden={isNextMerged} />
          )
          : null}

        <Box
          bg={isOwnNote ? 'neutral.200' : 'card.bg.1'}
          borderColor={isOwnNote ? 'accent.blue-text' : 'neutral.200'}
          borderWidth='1px'
          borderTopLeftRadius={isPrevMerged && !isOwnNote ? 0 : 2}
          borderTopRightRadius={isPrevMerged && isOwnNote ? 0 : 2}
          borderBottomLeftRadius={!isOwnNote ? 0 : 2}
          borderBottomRightRadius={isOwnNote ? 0 : 2}
          boxShadow='primary.s'
          maxW={{ base: 'calc(100% - 40px)', tabSm: '72%' }}
          minW={0}
          px={3}
          py={2}
        >
          {!isPrevMerged
            ? (
              <HStack spacing={2} flexWrap='wrap' mb={noteTitle ? 1 : 1.5}>
                {note.createdBy
                  ? (
                    <Text textStyle='bodyS' color='accent.blue-text'>
                      {`@${note.createdBy}`}
                    </Text>
                  )
                  : null}
                <Text textStyle='bodyS' color='gray.500'>
                  {formatTime(note.createdAt)}
                </Text>
                {isEdited(note)
                  ? (
                    <Text textStyle='bodyS' color='gray.500'>
                      edited
                    </Text>
                  )
                  : null}
              </HStack>
            )
            : null}

          {noteTitle
            ? (
              <Text
                textStyle='bodyMFat'
                color='graystrong.400'
                mb={isEditing || isTextNote || isAudioNote ? 1 : 0}
                noOfLines={1}
              >
                {noteTitle}
              </Text>
            )
            : null}

          {isEditing
            ? (
              <Textarea
                value={editingNote?.content || ''}
                onChange={event => onEditContentChange(event.target.value)}
                resize='vertical'
                minH='96px'
                bg='card.bg.1'
                isDisabled={isBusy}
              />
            )
            : isTextNote
              ? (
                <Text textStyle='bodyM' color='graystrong.400' whiteSpace='pre-wrap'>
                  {note.content}
                </Text>
              )
              : isAudioNote
                ? (
                  <AudioNote note={note} />
                )
                : (
                  <Text textStyle='bodyS' color='gray.500'>
                    Attachment unavailable
                  </Text>
                )}
        </Box>

        {isOwnNote
          ? (
            <NoteAvatar note={note} isHidden={isNextMerged} />
          )
          : null}
      </Flex>

      {!isEditing && hasActions
        ? (
          <HStack
            bg='card.bg.1'
            borderColor='neutral.200'
            borderRadius={2}
            borderWidth='1px'
            boxShadow='primary.s'
            left={isOwnNote ? 2 : undefined}
            opacity={0}
            pointerEvents='none'
            position='absolute'
            px={1}
            right={isOwnNote ? undefined : 2}
            spacing={1}
            top={isPrevMerged && !isEditing ? 0 : 1}
            transition='opacity 120ms ease'
            zIndex={2}
            _groupFocusWithin={{ opacity: 1, pointerEvents: 'auto' }}
            _groupHover={{ opacity: 1, pointerEvents: 'auto' }}
          >
            {canCopy
              ? (
                <Tooltip label='Copy note'>
                  <IconButton
                    aria-label='Copy note'
                    icon={<CopyIcon />}
                    size='sm'
                    variant='ghost'
                    colorScheme='gray'
                    isDisabled={isBusy}
                    onClick={onCopy}
                  />
                </Tooltip>
              )
              : null}
            {canMutate && isTextNote && !isEditing
              ? (
                <Tooltip label='Edit note'>
                  <IconButton
                    aria-label='Edit note'
                    icon={<EditIcon />}
                    size='sm'
                    variant='ghost'
                    colorScheme='gray'
                    isDisabled={isBusy}
                    onClick={onEdit}
                  />
                </Tooltip>
              )
              : null}
            {canMutate
              ? (
                <Tooltip label='Delete note'>
                  <IconButton
                    aria-label='Delete note'
                    icon={<TrashIcon />}
                    size='sm'
                    variant='ghost'
                    colorScheme='gray'
                    isDisabled={isBusy}
                    onClick={onDelete}
                  />
                </Tooltip>
              )
              : null}
          </HStack>
        )
        : null}

      {isEditing
        ? (
          <HStack
            justifyContent={isOwnNote ? 'flex-end' : 'flex-start'}
            spacing={2}
            mt={2}
            pl={isOwnNote ? 0 : 10}
            pr={isOwnNote ? 10 : 0}
          >
            <Button
              size='sm'
              variant='ghost'
              onClick={onCancelEdit}
              isDisabled={isBusy}
            >
              Cancel
            </Button>
            <Button
              size='sm'
              colorScheme='blue'
              onClick={onSave}
              isLoading={isBusy}
              isDisabled={!editingNote?.content.trim()}
            >
              Save
            </Button>
          </HStack>
        )
        : null}
    </Box>
  )
}

const NoteAvatar = ({
  note,
  isHidden,
}: {
  note: MarketingListAttachment
  isHidden: boolean
}) => (
  <Box
    boxSize='7'
    flexShrink={0}
    visibility={isHidden ? 'hidden' : 'visible'}
  >
    {isHidden
      ? null
      : note.createdBy
        ? (
          <UserAvatar
            id={note.createdBy}
            colorScheme='specialsat.700'
            size='lg'
          />
        )
        : (
          <Avatar
            colorScheme='specialsat.700'
            size='lg'
            src={GENERIC_AVATAR_URL}
          />
        )}
  </Box>
)

const AudioNote = ({ note }: { note: MarketingListAudioAttachment }) => {
  const audioUrl = getAttachmentUrl(note.link)

  return (
    <VStack spacing={2} align='stretch'>
      <HStack spacing={2}>
        {note.duration
          ? (
            <Text textStyle='bodyS' color='gray.500'>
              {formatDuration(note.duration)}
            </Text>
          )
          : null}
      </HStack>
      {audioUrl
        ? (
          <Box as='audio' controls src={audioUrl} w='full' />
        )
        : (
          <Text textStyle='bodyS' color='gray.500'>
            Audio unavailable
          </Text>
        )}
    </VStack>
  )
}

const groupNotes = (notes: MarketingListAttachment[]): NoteGroup[] => {
  const groups: NoteGroup[] = []

  notes
    .slice()
    .sort((a, b) => getTime(a.createdAt) - getTime(b.createdAt))
    .forEach(note => {
      const date = note.createdAt ? dayjs(note.createdAt) : null
      const key = date?.isValid() ? date.format('YYYY-MM-DD') : 'unknown'
      const label = date?.isValid() ? date.format('MMMM D, YYYY') : 'Undated'
      const existingGroup = groups.find(group => group.key === key)

      if (existingGroup) {
        existingGroup.notes.push(note)
        return
      }

      groups.push({
        key,
        label,
        notes: [note],
      })
    })

  return groups
}

const getNoteTitle = (note: MarketingListAttachment): string | null => {
  const name = note.name?.trim()
  if (name) return name
  if (note.__typename === 'MarketingListAudioAttachment') return 'Audio note'
  return null
}

const copyNote = async (note: MarketingListAttachment) => {
  if (note.__typename !== 'MarketingListTextAttachment') return
  if (!note.content) return
  if (!navigator.clipboard?.writeText)
    throw new Error('Clipboard is unavailable.')


  await navigator.clipboard.writeText(note.content)
  toast.success({
    title: 'Note Copied',
  })
}

const getAttachmentMutationInput = (
  note: MarketingListAttachment,
  parcelId: string,
): AttachmentMutationInput => ({
  parcelId: note.parcelId || parcelId,
  noteId: note.noteId,
})

const getAttachmentUrl = (link: string | null): string | null => {
  if (!link) return null
  if (/^https?:\/\//.test(link)) return link

  return `${API_URL}${link.startsWith('/') ? '' : '/'}${link}`
}

const formatTime = (value: string | null): string => {
  if (!value) return ''

  const date = dayjs(value)
  return date.isValid() ? date.format('h:mm A') : ''
}

const formatDuration = (durationMs: number): string => {
  const seconds = Math.max(0, Math.round(durationMs / 1000))
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = seconds % 60

  return `${minutes}:${`${remainingSeconds}`.padStart(2, '0')}`
}

const getTime = (value: string | null): number => {
  if (!value) return 0

  const timestamp = new Date(value).getTime()
  return Number.isFinite(timestamp) ? timestamp : 0
}

const getNoteMergeState = (
  notes: MarketingListAttachment[],
  index: number,
): { isPrevMerged: boolean, isNextMerged: boolean } => {
  const note = notes[index]
  if (!note) return { isPrevMerged: false, isNextMerged: false }

  return {
    isPrevMerged: canMergeNotes(notes[index - 1], note),
    isNextMerged: canMergeNotes(note, notes[index + 1]),
  }
}

// Match the mobile notes thread: consecutive notes from the same creator in
// the same minute share the author/time row and avatar spacing.
const canMergeNotes = (
  current: MarketingListAttachment | undefined,
  next: MarketingListAttachment | undefined,
): boolean =>
  !!current?.createdBy
  && !!next?.createdBy
  && areIdsEqual(current.createdBy, next.createdBy)
  && !!current.createdAt
  && !!next.createdAt
  && Math.abs(dayjs(current.createdAt).diff(next.createdAt, 'minute')) <= 0

const areIdsEqual = (
  left: string | null | undefined,
  right: string | null | undefined,
): boolean => {
  const normalizedLeft = normalizeComparableId(left)
  const normalizedRight = normalizeComparableId(right)

  return !!normalizedLeft
    && !!normalizedRight
    && normalizedLeft === normalizedRight
}

const normalizeComparableId = (value: string | null | undefined): string =>
  (value || '').trim().replace(/^0+/, '').toLowerCase()

const isEdited = (note: MarketingListAttachment): boolean => {
  if (!note.createdAt || !note.updatedAt) return false

  return Math.abs(dayjs(note.updatedAt).diff(note.createdAt, 'second')) > 1
}

const getMutationErrorMessage = (
  payload: MutationError | null | undefined,
  fallback: string,
): string => payload?.message || fallback

const reportSaveError = (error: unknown) => {
  toast.error({
    title: 'Unable to Save Note',
    message: error instanceof Error ? error.message : 'Please try again.',
  })
}

const reportCreateError = reportSaveError

const reportDeleteError = (error: unknown) => {
  toast.error({
    title: 'Unable to Delete Note',
    message: error instanceof Error ? error.message : 'Please try again.',
  })
}

const reportCopyError = (error: unknown) => {
  toast.error({
    title: 'Unable to Copy Note',
    message: error instanceof Error ? error.message : 'Please try again.',
  })
}
